/**
 * Pure document edits.
 *
 * Every change to a document goes through one of these functions. They return a
 * new document that structurally shares everything they did not touch, which
 * keeps undo snapshots cheap and lets React re-render only what changed.
 */

import type {
  CrowdDocument,
  DocumentSettings,
  FurnitureItem,
  Opening,
  ItineraryStep,
  Plan,
  PlanObjectRef,
  Population,
  Scenario,
  ServicePoint,
  Wall,
  Zone,
  Backdrop,
} from '../model/types'
import { wallLength } from '../model/planGeometry'
import { OPENING_JAMB } from '../model/standards'

type PlanPatch = Partial<Plan>

const withPlan = (doc: CrowdDocument, patch: PlanPatch): CrowdDocument => ({
  ...doc,
  plan: { ...doc.plan, ...patch },
  updatedAt: new Date().toISOString(),
})

const replaceById = <T extends { id: string }>(items: T[], id: string, patch: Partial<T>): T[] => {
  let changed = false
  const next = items.map((item) => {
    if (item.id !== id) return item
    changed = true
    return { ...item, ...patch }
  })
  return changed ? next : items
}

// --- walls -------------------------------------------------------------------

export const addWall = (doc: CrowdDocument, wall: Wall): CrowdDocument =>
  withPlan(doc, { walls: [...doc.plan.walls, wall] })

export const addWalls = (doc: CrowdDocument, walls: Wall[]): CrowdDocument =>
  walls.length === 0 ? doc : withPlan(doc, { walls: [...doc.plan.walls, ...walls] })

/**
 * An opening cut to fit the wall it is in.
 *
 * A door is cut into a wall, not instead of one: something has to carry the
 * head and a leaf needs a jamb to hang from, so a sliver of wall survives
 * either side. Without that the width could be the whole wall, which deletes
 * the wall from the plan without deleting it from the document; the offset
 * could push an opening off the end; and a doorway could stand taller than the
 * wall it was cut into.
 *
 * Every path that can change either the opening or its wall goes through this,
 * because a plan arrives from a file and a tool as well as from the inspector.
 */
const fitToWall = (opening: Opening, wall: Wall): Opening => {
  const length = wallLength(wall)
  const width = Math.max(0.05, Math.min(opening.width, length - 2 * OPENING_JAMB))
  const half = width / 2
  const offset = Math.min(
    Math.max(opening.offset, Math.min(half + OPENING_JAMB, length / 2)),
    Math.max(Math.min(half + OPENING_JAMB, length / 2), length - half - OPENING_JAMB),
  )
  const sill = Math.max(0, Math.min(opening.sill, Math.max(0, wall.height - 0.1)))
  const height = Math.max(0.1, Math.min(opening.height, wall.height - sill))
  return width === opening.width &&
    offset === opening.offset &&
    sill === opening.sill &&
    height === opening.height
    ? opening
    : { ...opening, width, offset, sill, height }
}

/** Re-fit every opening in a wall, leaving the array identical if none moved. */
const fitOpenings = (openings: readonly Opening[], wall: Wall): Opening[] | null => {
  let changed = false
  const next = openings.map((opening) => {
    if (opening.wallId !== wall.id) return opening
    const fitted = fitToWall(opening, wall)
    if (fitted !== opening) changed = true
    return fitted
  })
  return changed ? next : null
}

export const updateWall = (doc: CrowdDocument, id: string, patch: Partial<Wall>): CrowdDocument => {
  const walls = replaceById(doc.plan.walls, id, patch)
  if (walls === doc.plan.walls) return doc
  const wall = walls.find((w) => w.id === id)
  if (!wall) return withPlan(doc, { walls })
  const openings = fitOpenings(doc.plan.openings, wall)
  return withPlan(doc, openings ? { walls, openings } : { walls })
}

// --- openings ----------------------------------------------------------------

export const addOpening = (doc: CrowdDocument, opening: Opening): CrowdDocument => {
  const wall = doc.plan.walls.find((w) => w.id === opening.wallId)
  const fitted = wall ? fitToWall(opening, wall) : opening
  return withPlan(doc, { openings: [...doc.plan.openings, fitted] })
}

export const updateOpening = (
  doc: CrowdDocument,
  id: string,
  patch: Partial<Opening>,
): CrowdDocument => {
  const openings = replaceById(doc.plan.openings, id, patch)
  if (openings === doc.plan.openings) return doc
  const changed = openings.find((opening) => opening.id === id)
  const wall = changed && doc.plan.walls.find((w) => w.id === changed.wallId)
  if (!changed || !wall) return withPlan(doc, { openings })
  const fitted = fitToWall(changed, wall)
  return withPlan(doc, {
    openings: fitted === changed ? openings : openings.map((o) => (o.id === id ? fitted : o)),
  })
}

// --- furniture ---------------------------------------------------------------

export const addFurniture = (doc: CrowdDocument, items: FurnitureItem[]): CrowdDocument =>
  items.length === 0 ? doc : withPlan(doc, { furniture: [...doc.plan.furniture, ...items] })

export const updateFurniture = (
  doc: CrowdDocument,
  id: string,
  patch: Partial<FurnitureItem>,
): CrowdDocument => {
  const furniture = replaceById(doc.plan.furniture, id, patch)
  return furniture === doc.plan.furniture ? doc : withPlan(doc, { furniture })
}

// --- zones -------------------------------------------------------------------

export const addZone = (doc: CrowdDocument, zone: Zone): CrowdDocument =>
  withPlan(doc, { zones: [...doc.plan.zones, zone] })

export const updateZone = (doc: CrowdDocument, id: string, patch: Partial<Zone>): CrowdDocument => {
  const zones = replaceById(doc.plan.zones, id, patch)
  return zones === doc.plan.zones ? doc : withPlan(doc, { zones })
}

// --- service points ----------------------------------------------------------

export const addServicePoint = (doc: CrowdDocument, point: ServicePoint): CrowdDocument =>
  withPlan(doc, { servicePoints: [...doc.plan.servicePoints, point] })

export const updateServicePoint = (
  doc: CrowdDocument,
  id: string,
  patch: Partial<ServicePoint>,
): CrowdDocument => {
  const servicePoints = replaceById(doc.plan.servicePoints, id, patch)
  return servicePoints === doc.plan.servicePoints ? doc : withPlan(doc, { servicePoints })
}

// --- backdrop ----------------------------------------------------------------

export const setBackdrop = (doc: CrowdDocument, backdrop: Backdrop | undefined): CrowdDocument => {
  const plan = { ...doc.plan }
  if (backdrop) plan.backdrop = backdrop
  else delete plan.backdrop
  return { ...doc, plan, updatedAt: new Date().toISOString() }
}

export const updateBackdrop = (doc: CrowdDocument, patch: Partial<Backdrop>): CrowdDocument =>
  doc.plan.backdrop ? setBackdrop(doc, { ...doc.plan.backdrop, ...patch }) : doc

// --- generic -----------------------------------------------------------------

/** Apply a patch to any plan object, dispatching on its kind. */
export const updateObject = (
  doc: CrowdDocument,
  ref: PlanObjectRef,
  patch: Record<string, unknown>,
): CrowdDocument => {
  switch (ref.kind) {
    case 'wall':
      return updateWall(doc, ref.id, patch as Partial<Wall>)
    case 'opening':
      return updateOpening(doc, ref.id, patch as Partial<Opening>)
    case 'furniture':
      return updateFurniture(doc, ref.id, patch as Partial<FurnitureItem>)
    case 'zone':
      return updateZone(doc, ref.id, patch as Partial<Zone>)
    case 'service':
      return updateServicePoint(doc, ref.id, patch as Partial<ServicePoint>)
    case 'backdrop':
      return updateBackdrop(doc, patch as Partial<Backdrop>)
  }
}

/** Remove a set of objects, cascading openings when their wall goes. */
export const removeObjects = (
  doc: CrowdDocument,
  refs: readonly PlanObjectRef[],
): CrowdDocument => {
  if (refs.length === 0) return doc
  const wallIds = new Set(refs.filter((r) => r.kind === 'wall').map((r) => r.id))
  const openingIds = new Set(refs.filter((r) => r.kind === 'opening').map((r) => r.id))
  const furnitureIds = new Set(refs.filter((r) => r.kind === 'furniture').map((r) => r.id))
  const zoneIds = new Set(refs.filter((r) => r.kind === 'zone').map((r) => r.id))
  const serviceIds = new Set(refs.filter((r) => r.kind === 'service').map((r) => r.id))
  const dropBackdrop = refs.some((r) => r.kind === 'backdrop')

  // Arrays nothing was taken from are kept as they were: the renderer diffs by
  // identity, and a delete that matched nothing must hand back the document
  // itself or it costs an undo step and marks the file dirty.
  const keep = <T>(items: T[], drop: (item: T) => boolean): T[] => {
    const next = items.filter((item) => !drop(item))
    return next.length === items.length ? items : next
  }
  const plan: Plan = {
    walls: keep(doc.plan.walls, (w) => wallIds.has(w.id)),
    openings: keep(doc.plan.openings, (o) => openingIds.has(o.id) || wallIds.has(o.wallId)),
    furniture: keep(doc.plan.furniture, (f) => furnitureIds.has(f.id)),
    zones: keep(doc.plan.zones, (z) => zoneIds.has(z.id)),
    servicePoints: keep(doc.plan.servicePoints, (s) => serviceIds.has(s.id)),
  }
  if (doc.plan.backdrop && !dropBackdrop) plan.backdrop = doc.plan.backdrop
  const untouched =
    plan.walls === doc.plan.walls &&
    plan.openings === doc.plan.openings &&
    plan.furniture === doc.plan.furniture &&
    plan.zones === doc.plan.zones &&
    plan.servicePoints === doc.plan.servicePoints &&
    plan.backdrop === doc.plan.backdrop
  if (untouched) return doc

  // Itineraries and entry lists can reference deleted zones, counters — or
  // doors, since a door marked as a way in or out is a destination in its own
  // right. Openings that go with their wall count as deleted too, which is how
  // a population ends up pointing at a doorway nobody meant to remove.
  const goneOpenings = doc.plan.openings
    .filter((opening) => openingIds.has(opening.id) || wallIds.has(opening.wallId))
    .map((opening) => opening.id)
  const removedTargets = new Set([...zoneIds, ...serviceIds, ...goneOpenings])

  const populations = doc.scenario.populations.map((pop) => {
    const entryIds = pop.entryIds.filter((id) => !removedTargets.has(id))
    let changed = entryIds.length !== pop.entryIds.length
    const itinerary: ItineraryStep[] = []
    for (const step of pop.itinerary) {
      if (step.targetIds) {
        const targetIds = step.targetIds.filter((id) => !removedTargets.has(id))
        if (targetIds.length === step.targetIds.length) {
          itinerary.push(step)
          continue
        }
        changed = true
        // A step that named counters and has none left has nothing to do. The
        // same step written with a single `targetId` is dropped for exactly
        // that reason, and two spellings of one thing must not disagree.
        if (targetIds.length > 0) itinerary.push({ ...step, targetIds })
        continue
      }
      if (step.targetId && removedTargets.has(step.targetId)) {
        changed = true
        continue
      }
      itinerary.push(step)
    }
    // Deciding this on lengths alone missed the case that matters: pruning ids
    // out of a step does not change how many steps there are, so the pruned
    // itinerary was computed and then thrown away, and the plan lost a counter
    // the itinerary still named.
    return changed ? { ...pop, entryIds, itinerary } : pop
  })

  return {
    ...doc,
    plan,
    scenario: { ...doc.scenario, populations },
    updatedAt: new Date().toISOString(),
  }
}

// --- scenario ----------------------------------------------------------------

export const updateScenario = (doc: CrowdDocument, patch: Partial<Scenario>): CrowdDocument => ({
  ...doc,
  scenario: { ...doc.scenario, ...patch },
  updatedAt: new Date().toISOString(),
})

export const updatePopulation = (
  doc: CrowdDocument,
  id: string,
  patch: Partial<Population>,
): CrowdDocument => {
  const populations = replaceById(doc.scenario.populations, id, patch)
  return populations === doc.scenario.populations ? doc : updateScenario(doc, { populations })
}

export const addPopulation = (doc: CrowdDocument, population: Population): CrowdDocument =>
  updateScenario(doc, { populations: [...doc.scenario.populations, population] })

export const removePopulation = (doc: CrowdDocument, id: string): CrowdDocument => {
  const populations = doc.scenario.populations.filter((p) => p.id !== id)
  return populations.length === doc.scenario.populations.length
    ? doc
    : updateScenario(doc, { populations })
}

export const updateSettings = (
  doc: CrowdDocument,
  patch: Partial<DocumentSettings>,
): CrowdDocument => ({
  ...doc,
  settings: { ...doc.settings, ...patch },
  updatedAt: new Date().toISOString(),
})

export const renameDocument = (doc: CrowdDocument, name: string): CrowdDocument => ({
  ...doc,
  name,
  updatedAt: new Date().toISOString(),
})

// --- lookup helpers ----------------------------------------------------------

export const findObject = (doc: CrowdDocument, ref: PlanObjectRef) => {
  switch (ref.kind) {
    case 'wall':
      return doc.plan.walls.find((w) => w.id === ref.id)
    case 'opening':
      return doc.plan.openings.find((o) => o.id === ref.id)
    case 'furniture':
      return doc.plan.furniture.find((f) => f.id === ref.id)
    case 'zone':
      return doc.plan.zones.find((z) => z.id === ref.id)
    case 'service':
      return doc.plan.servicePoints.find((s) => s.id === ref.id)
    case 'backdrop':
      return doc.plan.backdrop
  }
}

/** A locked door or window hung in this wall, if there is one. */
export const lockedOpeningIn = (doc: CrowdDocument, wallId: string): Opening | undefined =>
  doc.plan.openings.find((opening) => opening.wallId === wallId && opening.locked)

export const isLocked = (doc: CrowdDocument, ref: PlanObjectRef): boolean => {
  const object = findObject(doc, ref)
  if (object && 'locked' in object && object.locked) return true
  // A door is placed along its wall, so moving or turning the wall moves and
  // turns the door. A locked door in an unlocked wall only refused deletion.
  return ref.kind === 'wall' && lockedOpeningIn(doc, ref.id) !== undefined
}
